/**
 * O que faz: Lista todos os artigos aprovados do blog publico com agrupamento por categoria.
 * Onde usa: Rota publica `/blog`.
 * Conexoes: Consome `publicContent` gerado e injeta schema Organization/WebSite para SEO.
 * Tabelas: blog_posts (SELECT publico filtrado por status approved via useBlogPosts).
 * Data: 11/05/2026
 */

import { Helmet } from "react-helmet-async";
import { useMemo, useState } from "react";
import { ArrowRight, BookOpenCheck, Bot, CheckCircle2, Layers3, Search } from "lucide-react";
import { Link } from "react-router-dom";
import PublicContentLayout from "@/components/blog/PublicContentLayout";
import ContentCard from "@/components/blog/ContentCard";
import { StructuredData, buildOrganizationSchema, buildWebsiteSchema } from "@/components/seo/StructuredData";
import { mapBlogPostToPublicContentItem } from "@/content/blogPostMapper";
import { useBlogPosts } from "@/hooks/useBlogPosts";

const ALL_CATEGORIES = "Todos";

const BlogIndexPage = () => {
  const { data: posts = [], isError, isLoading } = useBlogPosts();
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORIES);
  const blogItems = useMemo(
    () => posts.filter((item) => item.type === "blog").map(mapBlogPostToPublicContentItem),
    [posts],
  );
  const categories = useMemo(() => Array.from(new Set(blogItems.map((item) => item.category))), [blogItems]);
  const filteredItems =
    activeCategory === ALL_CATEGORIES ? blogItems : blogItems.filter((item) => item.category === activeCategory);
  const [featuredItem, ...secondaryItems] = filteredItems;

  return (
    <PublicContentLayout
      title="Blog Gens CRM"
      description="Conteúdos para clínicas sobre CRM, WhatsApp, funil comercial, IA e conversão."
    >
      <Helmet>
        <title>Blog Gens CRM | CRM para Clínicas, WhatsApp e IA</title>
        <meta
          name="description"
          content="Aprenda como organizar atendimento, leads e agenda com conteúdos atualizados para clínicas odontológicas, médicas e estéticas."
        />
        <link rel="canonical" href="https://crm.agenciagens.com.br/blog" />
      </Helmet>
      <StructuredData data={buildOrganizationSchema()} />
      <StructuredData data={buildWebsiteSchema()} />

      <section className="public-blog-index-header">
        <div>
          <span className="public-index-eyebrow">
            <BookOpenCheck size={16} />
            Conteúdo prático para clínicas
          </span>
          <h2>Guias para vender, atender e reter pacientes com mais previsibilidade.</h2>
          <p>
            Conteúdos diretos para quem precisa lotar agenda, organizar atendimento e transformar interesse em paciente
            de forma consistente.
          </p>
        </div>
      </section>

      <section className="public-category-tabs" aria-label="Categorias do blog">
        {[ALL_CATEGORIES, ...categories].map((category) => {
          const total =
            category === ALL_CATEGORIES ? blogItems.length : blogItems.filter((item) => item.category === category).length;
          return (
            <button
              key={category}
              type="button"
              className={activeCategory === category ? "is-active" : undefined}
              onClick={() => setActiveCategory(category)}
            >
              {category}
              <span>{total}</span>
            </button>
          );
        })}
      </section>

      {isLoading ? (
        <section className="public-section">
          <p className="public-card-meta">Carregando conteúdos...</p>
        </section>
      ) : null}

      {isError ? (
        <section className="public-section">
          <h2>Não foi possível carregar o blog</h2>
          <p>Tente novamente em alguns instantes.</p>
        </section>
      ) : null}

      {!isLoading && !isError && blogItems.length === 0 ? (
        <section className="public-section">
          <h2>Nenhum artigo publicado ainda</h2>
          <p>Novos conteúdos aprovados pela equipe aparecerão aqui.</p>
        </section>
      ) : null}

      {!isLoading && !isError && featuredItem ? (
        <section className="public-blog-index-layout">
          <div className="public-blog-index-main">
            <div className="public-section-heading">
              <div>
                <span>{activeCategory === ALL_CATEGORIES ? "Todos os artigos" : activeCategory}</span>
                <h2>{filteredItems.length} conteúdo{filteredItems.length === 1 ? "" : "s"} para aplicar na clínica</h2>
              </div>
              <Search size={22} />
            </div>

            <ContentCard item={featuredItem} variant="featured" />

            {secondaryItems.length > 0 ? (
              <div className="public-grid public-blog-card-grid">
                {secondaryItems.map((item) => (
                  <ContentCard key={item.slug} item={item} />
                ))}
              </div>
            ) : null}
          </div>

          <aside className="public-blog-index-sidebar">
            <section className="public-sidebar-card public-blog-category-card">
              <h2>Categorias</h2>
              <div>
                {categories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={activeCategory === category ? "is-active" : undefined}
                    onClick={() => setActiveCategory(category)}
                  >
                    <span>{category}</span>
                    <strong>{blogItems.filter((item) => item.category === category).length}</strong>
                  </button>
                ))}
              </div>
            </section>

            <section className="public-sidebar-card public-blog-guide-card">
              <h2>Guias rápidos</h2>
              <Link to="/crm-para-dentistas">
                <Layers3 size={17} />
                CRM para dentistas
                <ArrowRight size={15} />
              </Link>
              <Link to="/crm-para-clinicas-medicas">
                <Bot size={17} />
                CRM para clínicas médicas
                <ArrowRight size={15} />
              </Link>
              <Link to="/crm-para-clinicas-esteticas">
                <CheckCircle2 size={17} />
                CRM para clínicas estéticas
                <ArrowRight size={15} />
              </Link>
            </section>
          </aside>
        </section>
      ) : null}
    </PublicContentLayout>
  );
};

export default BlogIndexPage;
